import type { AuthState } from "@/data/types/auth";
import { authService } from "@/services/auth/auth-service/auth-service";
import { authStorage } from "@/services/auth/auth-storage/auth-storage";
import type { Dispatch, SetStateAction } from "react";

type AuthActions = {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  handleCallback: () => Promise<void>;
  refreshTokens: () => Promise<boolean>;
  clearError: () => void;
};

export const useAuthActions = (
  setAuthState: Dispatch<SetStateAction<AuthState>>
): AuthActions => {
  const login = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      await authService.login();
      const [tokens, user] = await Promise.all([
        authStorage.getTokens(),
        authStorage.getUser(),
      ]);
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user,
        tokens,
        error: null,
      });
      authService.startProactiveRenewal();
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Login failed",
      }));
    }
  };

  const logout = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      await authService.logout();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: null,
      });
    } catch {
      authService.stopProactiveRenewal();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: null,
      });
    }
  };

  const handleCallback = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      await authService.handleCallback();
      const [tokens, user] = await Promise.all([
        authStorage.getTokens(),
        authStorage.getUser(),
      ]);
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user,
        tokens,
        error: null,
      });
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: error instanceof Error ? error.message : "Authentication failed",
      });
    }
  };

  const refreshTokens = async (): Promise<boolean> => {
    try {
      const refreshed = await authService.refreshTokens();
      if (refreshed) {
        const [tokens, user] = await Promise.all([
          authStorage.getTokens(),
          authStorage.getUser(),
        ]);
        setAuthState((prev) => ({ ...prev, tokens, user, error: null }));
      }
      return refreshed;
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Token refresh failed",
      }));
      return false;
    }
  };

  const clearError = () => {
    setAuthState((prev) => ({ ...prev, error: null }));
  };

  return { login, logout, handleCallback, refreshTokens, clearError };
};
